import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError, formatCurrency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Upload, ImageOff, X, FileSpreadsheet, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BACKEND } from "@/lib/api";

const empty = { name: "", description: "", price: "", photo_url: "", category: "General", modifiers: [], modifier_groups: [] };

export default function AdminMenu() {
  const { user } = useAuth();
  const expired = user?.subscription_active === false;
  const [dishes, setDishes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Excel import state
  const [openImport, setOpenImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importReplace, setImportReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const xlsxRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/dishes"); setDishes(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (d) => { setEditing(d); setForm({ name: d.name, description: d.description, price: d.price, photo_url: d.photo_url || "", category: d.category || "General", modifiers: d.modifiers || [], modifier_groups: d.modifier_groups || [] }); setOpen(true); };

  const addModifier = () => setForm(f => ({ ...f, modifiers: [...(f.modifiers || []), { name: "", price_delta: 0 }] }));
  const updateModifier = (idx, key, value) => setForm(f => {
    const next = [...(f.modifiers || [])];
    next[idx] = { ...next[idx], [key]: key === "price_delta" ? Number(value) : value };
    return { ...f, modifiers: next };
  });
  const removeModifier = (idx) => setForm(f => ({ ...f, modifiers: (f.modifiers || []).filter((_, i) => i !== idx) }));

  // Modifier groups
  const addGroup = () => setForm(f => ({
    ...f,
    modifier_groups: [...(f.modifier_groups || []), { name: "", type: "multi", required: false, options: [] }],
  }));
  const updateGroup = (gi, key, value) => setForm(f => {
    const next = [...(f.modifier_groups || [])];
    next[gi] = { ...next[gi], [key]: value };
    return { ...f, modifier_groups: next };
  });
  const removeGroup = (gi) => setForm(f => ({ ...f, modifier_groups: (f.modifier_groups || []).filter((_, i) => i !== gi) }));
  const addOption = (gi) => setForm(f => {
    const next = [...(f.modifier_groups || [])];
    next[gi] = { ...next[gi], options: [...(next[gi].options || []), { name: "", price_delta: 0 }] };
    return { ...f, modifier_groups: next };
  });
  const updateOption = (gi, oi, key, value) => setForm(f => {
    const next = [...(f.modifier_groups || [])];
    const opts = [...(next[gi].options || [])];
    opts[oi] = { ...opts[oi], [key]: key === "price_delta" ? Number(value) : value };
    next[gi] = { ...next[gi], options: opts };
    return { ...f, modifier_groups: next };
  });
  const removeOption = (gi, oi) => setForm(f => {
    const next = [...(f.modifier_groups || [])];
    next[gi] = { ...next[gi], options: (next[gi].options || []).filter((_, i) => i !== oi) };
    return { ...f, modifier_groups: next };
  });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, photo_url: data.url }));
      toast.success("Foto subida");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const modifiers = (form.modifiers || [])
        .filter(m => (m.name || "").trim())
        .map(m => ({ ...m, price_delta: Number(m.price_delta || 0) }));
      const modifier_groups = (form.modifier_groups || [])
        .filter(g => (g.name || "").trim() || (g.options || []).some(o => (o.name || "").trim()))
        .map(g => ({
          ...g,
          name: (g.name || "Opciones").trim(),
          options: (g.options || []).filter(o => (o.name || "").trim()).map(o => ({ ...o, price_delta: Number(o.price_delta || 0) })),
        }));
      const payload = { ...form, price: parseFloat(form.price), modifiers, modifier_groups };
      if (editing) { await api.put(`/admin/dishes/${editing.id}`, payload); toast.success("Platillo actualizado"); }
      else { await api.post("/admin/dishes", payload); toast.success("Platillo creado"); }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/dishes/${id}`); toast.success("Platillo eliminado"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/dishes/template", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "oRdeN-plantilla-menu.xlsx";
      a.rel = "noopener";
      a.target = "_self";
      document.body.appendChild(a);
      a.click();
      // Defer cleanup so Safari/Firefox don't cancel the download
      setTimeout(() => {
        try { document.body.removeChild(a); } catch { /* noop */ }
        window.URL.revokeObjectURL(url);
      }, 1500);
      toast.success("Plantilla descargada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo descargar la plantilla");
    }
  };

  const runImport = async () => {
    if (!importFile) {
      toast.error("Elige un archivo .xlsx");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post(`/admin/dishes/import?replace=${importReplace}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`${data.created_count} platillo(s) creado(s)${data.error_count ? `, ${data.error_count} con error` : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  return (
    <div className="animate-fade-up" data-testid="admin-menu-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2">Carta</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1a0f2e] tracking-tight">Menú</h1>
          <p className="text-[#7248ae] mt-2">Agrega, edita y organiza tus platillos con foto y precio.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={() => { setImportResult(null); setImportFile(null); setOpenImport(true); }}
            disabled={expired}
            className="border-[#b182ec]/50 text-[#3e166c]"
            data-testid="open-import-button"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />Cargar desde Excel
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startCreate} disabled={expired} className="bg-[#3e166c] hover:bg-[#7248ae] text-white rounded-xl" data-testid="new-dish-button">
                <Plus className="h-4 w-4 mr-2" />Nuevo platillo
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar platillo" : "Nuevo platillo"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="dish-form">
              <div className="flex gap-4">
                <div className="w-32 h-32 rounded-xl overflow-hidden bg-[#fcfbfd] border border-[#b182ec]/30 flex items-center justify-center">
                  {form.photo_url ? (
                    <img src={fileUrl(form.photo_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImageOff className="h-8 w-8 text-[#b182ec]" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} data-testid="dish-photo-input" />
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full border-[#b182ec]/50" data-testid="upload-photo-button">
                    <Upload className="h-4 w-4 mr-2" />{uploading ? "Subiendo…" : "Subir foto"}
                  </Button>
                  <p className="text-xs text-[#7248ae]">JPG, PNG hasta 5MB.</p>
                </div>
              </div>
              <div><Label>Nombre</Label><Input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="dish-name-input" /></div>
              <div><Label>Descripción</Label><Textarea rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} data-testid="dish-description-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Precio (MXN)</Label><Input type="number" step="0.01" min={0} required value={form.price} onChange={e=>setForm({...form,price:e.target.value})} data-testid="dish-price-input" /></div>
                <div><Label>Categoría</Label><Input value={form.category} onChange={e=>setForm({...form,category:e.target.value})} data-testid="dish-category-input" /></div>
              </div>

              <div className="pt-2 border-t border-[#b182ec]/30">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider text-[#7248ae]">Modificadores (extras)</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addModifier} className="h-7 border-[#b182ec]/50" data-testid="add-modifier-button">
                    <Plus className="h-3 w-3 mr-1" />Agregar
                  </Button>
                </div>
                {(form.modifiers || []).length === 0 && (
                  <p className="text-xs text-[#7248ae]">Ej: "Extra queso +$15", "Sin cebolla $0".</p>
                )}
                <div className="space-y-2" data-testid="modifiers-list">
                  {(form.modifiers || []).map((m, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`modifier-row-${idx}`}>
                      <Input
                        placeholder="Nombre (Ej: Extra queso)"
                        value={m.name}
                        onChange={e => updateModifier(idx, "name", e.target.value)}
                        className="flex-1"
                        data-testid={`modifier-name-${idx}`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0"
                        value={m.price_delta}
                        onChange={e => updateModifier(idx, "price_delta", e.target.value)}
                        className="w-24"
                        data-testid={`modifier-price-${idx}`}
                      />
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeModifier(idx)} className="h-8 w-8 text-[#e11d48]" data-testid={`remove-modifier-${idx}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-2 border-t border-[#b182ec]/30">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs uppercase tracking-wider text-[#7248ae]">Grupos de modificadores</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addGroup} className="h-7 border-[#b182ec]/50" data-testid="add-group-button">
                    <Plus className="h-3 w-3 mr-1" />Nuevo grupo
                  </Button>
                </div>
                {(form.modifier_groups || []).length === 0 && (
                  <p className="text-xs text-[#7248ae]">Crea grupos para opciones tipo radio (un solo valor: término de cocción) o checkbox (múltiples: extras).</p>
                )}
                <div className="space-y-3" data-testid="groups-list">
                  {(form.modifier_groups || []).map((g, gi) => (
                    <div key={gi} className="p-3 rounded-lg bg-[#fcfbfd] border border-[#b182ec]/30" data-testid={`group-row-${gi}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Input
                          placeholder="Nombre del grupo (Ej: Término, Extras, Pan)"
                          value={g.name}
                          onChange={e => updateGroup(gi, "name", e.target.value)}
                          className="flex-1"
                          data-testid={`group-name-${gi}`}
                        />
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeGroup(gi)} className="h-8 w-8 text-[#e11d48]" data-testid={`remove-group-${gi}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-3 mb-3 text-xs">
                        <label className="inline-flex items-center gap-1.5">
                          <input
                            type="radio"
                            checked={g.type === "multi"}
                            onChange={() => updateGroup(gi, "type", "multi")}
                            data-testid={`group-type-multi-${gi}`}
                          /> Múltiple (checkbox)
                        </label>
                        <label className="inline-flex items-center gap-1.5">
                          <input
                            type="radio"
                            checked={g.type === "single"}
                            onChange={() => updateGroup(gi, "type", "single")}
                            data-testid={`group-type-single-${gi}`}
                          /> Único (radio)
                        </label>
                        <label className="inline-flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            checked={!!g.required}
                            onChange={e => updateGroup(gi, "required", e.target.checked)}
                            data-testid={`group-required-${gi}`}
                          /> Obligatorio
                        </label>
                      </div>
                      <div className="space-y-1.5">
                        {(g.options || []).map((o, oi) => (
                          <div key={oi} className="flex items-center gap-2" data-testid={`option-row-${gi}-${oi}`}>
                            <Input
                              placeholder="Opción (Ej: Término medio)"
                              value={o.name}
                              onChange={e => updateOption(gi, oi, "name", e.target.value)}
                              className="flex-1 h-8 text-sm"
                              data-testid={`option-name-${gi}-${oi}`}
                            />
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0"
                              value={o.price_delta}
                              onChange={e => updateOption(gi, oi, "price_delta", e.target.value)}
                              className="w-20 h-8 text-sm"
                              data-testid={`option-price-${gi}-${oi}`}
                            />
                            <Button type="button" size="icon" variant="ghost" onClick={() => removeOption(gi, oi)} className="h-7 w-7 text-[#e11d48]" data-testid={`remove-option-${gi}-${oi}`}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                        <Button type="button" size="sm" variant="ghost" onClick={() => addOption(gi)} className="h-7 text-[#3e166c]" data-testid={`add-option-${gi}`}>
                          <Plus className="h-3 w-3 mr-1" />Agregar opción
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter><Button type="submit" className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="submit-dish-button">{editing ? "Guardar" : "Crear"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={openImport} onOpenChange={setOpenImport}>
          <DialogContent className="max-w-lg" data-testid="import-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl">Cargar menú desde Excel</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-[#3e166c]/5 border border-[#b182ec]/30 text-sm text-[#1a0f2e]">
                <p className="font-semibold mb-1">Formato esperado</p>
                <p className="text-[#7248ae] mb-3">Columnas: <strong className="text-[#1a0f2e]">Nombre · Descripción · Precio · Categoría · URL Foto (opcional)</strong>.</p>
                <Button type="button" size="sm" variant="outline" onClick={downloadTemplate} className="border-[#3e166c]/40 text-[#3e166c]" data-testid="download-template-button">
                  <Download className="h-4 w-4 mr-2" />Descargar plantilla
                </Button>
              </div>

              <div>
                <input
                  ref={xlsxRef}
                  type="file"
                  accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  hidden
                  onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                  data-testid="import-file-input"
                />
                <Button type="button" variant="outline" onClick={() => xlsxRef.current?.click()} className="w-full border-[#b182ec]/50 h-12" data-testid="pick-file-button">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  {importFile ? importFile.name : "Elegir archivo .xlsx"}
                </Button>
              </div>

              <label className="flex items-center gap-2 text-sm text-[#1a0f2e] cursor-pointer" data-testid="replace-toggle">
                <input
                  type="checkbox"
                  checked={importReplace}
                  onChange={(e) => setImportReplace(e.target.checked)}
                  data-testid="replace-checkbox"
                />
                Reemplazar menú actual (elimina los platillos existentes antes de importar)
              </label>

              {importResult && (
                <div className="p-4 rounded-xl bg-[#fcfbfd] border border-[#b182ec]/30" data-testid="import-result">
                  <p className="text-[#1a0f2e] font-semibold mb-2">Resultado</p>
                  <p className="text-sm text-[#10b981]">{importResult.created_count} platillo(s) creado(s)</p>
                  {importResult.error_count > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-[#e11d48] mb-1">{importResult.error_count} con error</p>
                      <ul className="text-xs text-[#7248ae] space-y-1 max-h-32 overflow-y-auto" data-testid="import-errors">
                        {importResult.errors.map((e, i) => (
                          <li key={i}>Fila {e.row}: {e.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenImport(false)} className="border-[#b182ec]/50">Cerrar</Button>
              <Button type="button" disabled={importing || !importFile} onClick={runImport} className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="run-import-button">
                {importing ? "Importando…" : (<><Upload className="h-4 w-4 mr-2" />Importar</>)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dishes-grid">
        {dishes.map((d, i) => (
          <Card key={d.id} className={`overflow-hidden border border-[#b182ec]/30 rounded-2xl card-glow transition-all hover:-translate-y-1 animate-fade-up stagger-${(i%6)+1}`} data-testid={`dish-card-${d.name}`}>
            <div className="h-40 bg-[#3e166c]/10 relative">
              {d.photo_url ? (
                <img src={fileUrl(d.photo_url)} alt={d.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageOff className="h-10 w-10 text-[#b182ec]" />
                </div>
              )}
              <span className="absolute top-3 left-3 px-2 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] uppercase tracking-wider text-[#3e166c] font-semibold">{d.category}</span>
            </div>
            <div className="p-5">
              <h3 className="font-display text-lg font-bold text-[#1a0f2e]">{d.name}</h3>
              <p className="text-sm text-[#7248ae] mb-3 line-clamp-2">{d.description || "Sin descripción"}</p>
              <div className="flex items-center justify-between">
                <span className="font-display text-2xl font-black text-[#3e166c]">{formatCurrency(d.price)}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(d)} disabled={expired} className="border-[#b182ec]/50" data-testid={`edit-dish-${d.name}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" disabled={expired} className="border-[#e11d48]/40 text-[#e11d48] hover:bg-[#e11d48]/10" data-testid={`delete-dish-${d.name}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar &quot;{d.name}&quot;?</AlertDialogTitle>
                        <AlertDialogDescription>El platillo se quitará del menú. Los pedidos históricos quedan intactos.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction className="bg-[#e11d48] hover:bg-[#e11d48]/80" onClick={() => remove(d.id)}>Eliminar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
